import { Regions } from '@smh/projects/regions';
import { componentFactoryOf } from 'vue-tsx-support';

import {
  JtnUiTypography,
  JtnUiBtnIcon,
  JtnUiTextarea,
  JtnUiInput,
  JtnUiBtn,
  JtnUiFileuploader,
  JtnUiSuccessMessage,
  JtnUiCheckbox,
  JtnUiQuestion,
  JtnUiInformationBlock,
  JtnUiSpin
} from '@jtnews/jtn-ui';
import { injectStylesMixin, tsStoreMixin } from '@jtnews/shared';
import { HTML_TAGS_REGEX, EMAIL_REGEX, PHONE_REGEX } from '@jtnews/shared/data';

import styles from './send-opinion-form.styles.scss?module';

type ModalEvents = {
  onClosed(): void;
};

type ModalParams = {
  params: {
    subjectPostfix: string;
    reachGoalBlockType: string;
  };
};
type FormEvent = { target: HTMLInputElement };
type CatchError =
  | {
      data?: {
        status: string;
        detail: string;
      };
    }
  | undefined;

type ComponentData = {
  isFormSubmitted: boolean;
  isSendSuccessfully: boolean;
  isBindProfile: boolean;
  files: File[];
  maxMessageLength: number;
  message: string;
  userContacts: string;
  subjectPostfix: string;
  reachGoalBlockType: string;
  captchaToken: string;
  apiErrorMsg: string;
  isFetching: boolean;
};

const CLOSE_ICON_SIZE = 18;

export default componentFactoryOf<ModalEvents>()
  .mixin(injectStylesMixin(styles))
  .mixin(tsStoreMixin)
  .create({
    name: 'SendOpinionForm',
    props: {
      params: {
        type: Object as () => ModalParams,
        required: true
      }
    },
    data(): ComponentData {
      return {
        isFormSubmitted: false,
        isSendSuccessfully: false,
        isBindProfile: true,
        files: [],
        maxMessageLength: 6000,
        message: '',
        userContacts: '',
        subjectPostfix: '',
        reachGoalBlockType: '',
        captchaToken: '',
        apiErrorMsg: '',
        isFetching: false
      };
    },
    computed: {
      regionId(): Regions {
        return this.store.regionId;
      },
      isMobile(): boolean {
        return this.store.deviceInfo.isMobile && !this.store.deviceInfo.isTablet;
      },
      captchaText(): string {
        return this.store.commonModule.captchaText;
      },
      profileLink() {
        return this.store.commonModule.userInfo?.link ?? '';
      },
      contactsError(): string {
        if (!this.isFormSubmitted) {
          return '';
        }
        if (this.userContacts.trim().length === 0) {
          return 'Введите номер телефона или E-mail';
        }
        if (this.userContacts.trim().length < 5) {
          return 'Номер телефона или E-mail должны быть не меньше пяти символов';
        }
        if (
          EMAIL_REGEX.exec(this.userContacts) === null &&
          PHONE_REGEX.exec(this.userContacts) === null
        ) {
          return 'Введите корректный номер телефона или E-mail';
        }

        return '';
      },
      messageError(): string {
        if (!this.isFormSubmitted) {
          return '';
        }
        if (this.message.trim().length === 0) {
          return 'Введите текст сообщения';
        }
        if (this.message.trim().length < 3) {
          return 'Текст сообщения должен быть не меньше трех символов';
        }
        if (HTML_TAGS_REGEX.exec(this.message) !== null) {
          return 'Текст сообщения содержит недопустимые символы';
        }

        return '';
      },
      leftSlotSubmitBtnContent() {
        return this.isFetching ? (
          <JtnUiSpin slot="left" size="small" />
        ) : (
          <span slot="left"></span>
        );
      }
    },
    watch: {
      message(current: string) {
        if (current.length > this.maxMessageLength) {
          this.message = current.slice(0, this.maxMessageLength);
        }
      }
    },
    mounted() {
      this.beforeOpen(this.params);
    },
    methods: {
      beforeOpen(event: ModalParams) {
        const { params } = event;
        if (params === undefined || params === null) {
          return;
        }
        const { subjectPostfix = '', reachGoalBlockType = '' } = params;
        this.subjectPostfix = subjectPostfix;
        this.reachGoalBlockType = reachGoalBlockType;

        this.sendReachGoal('Просмотр', 'viewSendOpinionForm');
      },
      changeConnection() {
        this.sendReachGoal('Связать с профилем (галочка)', 'clickSendOpinionForm');
        this.isBindProfile = !this.isBindProfile;
      },
      generatingData(): FormData {
        const formData = new FormData();
        if (this.subjectPostfix !== '') {
          formData.append('subjectPostfix', this.subjectPostfix);
        }

        formData.append('content', this.message);
        formData.append('contact', this.userContacts);
        formData.append('regionId', this.regionId.toString());
        formData.append('bindProfile', this.isBindProfile ? '1' : '0');
        this.files.forEach((file, index) => {
          formData.append(`attaches[${index}]`, file);
        });
        formData.append('captchaToken', this.captchaToken);

        return formData;
      },
      async formSubmit(event: Event) {
        event.preventDefault();
        this.sendReachGoal('Отправить', 'clickSendOpinionForm');
        this.isFormSubmitted = true;
        if (this.contactsError.length > 0 || this.messageError.length > 0) {
          return;
        }

        this.isFetching = true;
        this.captchaToken = (await this.executeCaptchaToken()) || '';
        const formData = this.generatingData();
        void this.sendMyOpinion(formData);
      },
      async sendMyOpinion(formData: FormData) {
        this.apiErrorMsg = '';

        try {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
          await this.store.newsApiClient.sendMyOpinion(this.regionId, formData);

          this.isSendSuccessfully = true;
          this.subjectPostfix = '';
          this.apiErrorMsg = '';
        } catch (err) {
          console.error(err);
          const error = (err as CatchError)?.data || undefined;
          this.apiErrorMsg =
            typeof error !== 'undefined'
              ? error.detail
              : 'Не удалось отправить сообщение. Попробуйте еще раз.';

          this.isSendSuccessfully = false;
        } finally {
          this.isFetching = false;
        }
      },
      async executeCaptchaToken(): Promise<string | undefined> {
        try {
          return await this.store.commonModule.executeCaptchaToken('send_opinion');
        } catch (error) {
          console.error(error);
        }
      },
      closeModal() {
        this.clearForm();
        this.$emit('closed');
        this.sendReachGoal('Закрыть', 'clickSendOpinionForm');
        this.store.commonModule.updateAlertsClosingAllowedState(true);
      },
      clearForm() {
        this.userContacts = '';
        this.message = '';
        this.files = [];
        this.apiErrorMsg = '';
        this.captchaToken = '';
        this.isFormSubmitted = false;
        this.isSendSuccessfully = false;
      },
      sendReachGoal(valueName: string, goalName: string) {
        this.store.analyticsModule.sendNewReachGoal({
          blockType: this.reachGoalBlockType || 'Контент',
          fieldType: 'Форма отправки Мнения',
          valueName,
          goalName
        });
      }
    },
    render() {
      return (
        <section class={styles.sendOpinionModal}>
          <JtnUiBtnIcon
            class={styles.closeBtn}
            btn-type="button-other-type-3"
            size={CLOSE_ICON_SIZE}
            onClick={() => this.closeModal()}
          >
            <svg width={CLOSE_ICON_SIZE} height={CLOSE_ICON_SIZE}>
              <use xlinkHref="/dist/legacy/svg-sprites/jtn-critical.42b7545660e4f467e75d4b37a02533e6.svg#jtn-critical-close"></use>
            </svg>
          </JtnUiBtnIcon>
          <JtnUiTypography class={styles.title} tag="h3" type="font-format-news">
            Поделиться своим мнением
          </JtnUiTypography>
          {this.isSendSuccessfully ? (
            <JtnUiSuccessMessage text="Спасибо за ваше мнение. Редакция свяжется с вами" />
          ) : (
            <form
              class={styles.form}
              onSubmit={(event: Event) => void this.formSubmit(event)}
            >
              <JtnUiInformationBlock class={styles.confidentialtyText}>
                <p>
                  <b>
                    Пожалуйста, оставьте настоящие данные, чтобы мы могли связаться с вами
                    и уточнить информацию.
                  </b>{' '}
                  При необходимости мы гарантируем вам анонимность.
                </p>
              </JtnUiInformationBlock>
              <JtnUiInput
                id="opinion_num_or_email"
                class={styles.formInput}
                value={this.userContacts}
                required={true}
                hasError={this.contactsError.length > 0}
                label="Ваш номер телефона или e-mail"
                placeholder="Введите номер или e-mail"
                onInput={(event: FormEvent) => (this.userContacts = event.target.value)}
              >
                {this.contactsError.length > 0 && (
                  <span slot="error">{this.contactsError}</span>
                )}
              </JtnUiInput>
              <JtnUiTextarea
                id="opinion_message"
                class={styles.formTextarea}
                label="Введите текст сообщения"
                maxLength={this.maxMessageLength}
                value={this.message}
                required={true}
                hasError={this.messageError.length > 0}
                onInput={(event: FormEvent) => (this.message = event.target.value)}
              >
                {this.messageError.length > 0 && (
                  <span slot="error">{this.messageError}</span>
                )}
              </JtnUiTextarea>
              <JtnUiFileuploader
                class={styles.fileuploader}
                addedFiles={this.files}
                onFilesChanged={(files: File[]) => (this.files = files)}
                onClickedAddNewFile={() =>
                  this.sendReachGoal('Прикрепить файл', 'clickSendOpinionForm')
                }
              />
              <div class={styles.connectToProfile}>
                <JtnUiCheckbox
                  name="connect-to-profile"
                  checked={this.isBindProfile}
                  size="big"
                  onChange="changeConnection()"
                >
                  Связать с профилем пользователя
                </JtnUiCheckbox>
                <JtnUiQuestion
                  class={styles.connectInformer}
                  popupPosition={{ bottom: 'calc(100% + 5px)', right: '0' }}
                  onClickedQuestion={() =>
                    this.sendReachGoal(
                      'Связать с профилем (подсказка)',
                      'clickSendOpinionForm'
                    )
                  }
                >
                  <JtnUiTypography tag="p" linkType="link-blue">
                    Выберите этот пункт, если согласны, чтобы ваше мнение выводилось на
                    странице{' '}
                    <a href={this.profileLink} target="_blank">
                      вашего профиля.
                    </a>
                  </JtnUiTypography>
                </JtnUiQuestion>
              </div>
              <div class={styles.buttonsGroup}>
                <JtnUiBtn class={styles.btn} type="submit">
                  Отправить
                  {this.leftSlotSubmitBtnContent}
                </JtnUiBtn>
                <JtnUiTypography
                  class={styles.captchaSubmitNote}
                  tag="p"
                  type="font-vijet-type-1"
                  linkType="link-blue"
                >
                  <div domPropsInnerHTML={this.captchaText} />
                </JtnUiTypography>
              </div>

              {this.apiErrorMsg.length > 0 && (
                <JtnUiTypography class={styles.error} tag="p" link-type="link-red-bold">
                  <span domPropsInnerHTML={this.apiErrorMsg} />
                </JtnUiTypography>
              )}
            </form>
          )}
        </section>
      );
    }
  });
